import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';
import CityService, { City } from '../../services/city.service';

const LocationSelection: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  
  // Estado para las ciudades disponibles
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estado para las ubicaciones seleccionadas
  const [selectedLocations, setSelectedLocations] = useState<string[]>(companyData.locations || []);
  
  // Cargar las ciudades desde la API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const cities = await CityService.getAllCities();
        setAvailableCities(cities);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar las ciudades:', err);
        setError('No se pudieron cargar las ciudades. Por favor, intenta de nuevo más tarde.');
        setLoading(false);
      }
    };
    
    fetchCities();
  }, []);
  
  // Verificar si la categoría está seleccionada, si no, redirigir
  useEffect(() => {
    if (!companyData.category) {
      navigate('/onboarding/category');
    }
  }, [companyData.category, navigate]);
  
  // Manejar la selección de una ubicación
  const handleLocationSelect = (location: string) => {
    setSelectedLocations(prev => {
      // Si ya está seleccionada, la quitamos
      if (prev.includes(location)) {
        return prev.filter(loc => loc !== location);
      }
      // Si no está seleccionada, la agregamos
      return [...prev, location];
    });
  };
  
  // Manejar la selección de todas las ubicaciones
  const handleSelectAll = () => {
    if (selectedLocations.length === availableCities.length) {
      // Si todas están seleccionadas, deseleccionamos todas
      setSelectedLocations([]);
    } else {
      // Si no todas están seleccionadas, seleccionamos todas
      setSelectedLocations(availableCities.map(city => city.name));
    }
  };
  
  const handleBack = () => {
    setCurrentStep(5);
    navigate('/onboarding/company-history');
  };

  // Manejar el envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Actualizar los datos en el contexto
    updateCompanyData({
      locations: selectedLocations
    });
    
    setCurrentStep(7); // Avanzar al siguiente paso
    navigate('/onboarding/lead-price'); // Navegar a la página de selección de precio de leads
  };
  
  return (
    <OnboardingLayout
      title="¡Ubica tu talento en el mapa!"
      subtitle="Indícanos las zonas donde prestas tus servicios. Esta información es clave para que podamos mostrar tu empresa a los clientes correctos."
      showBackButton={true}
      onBack={handleBack}
    >
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-xervix-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-xervix-primary hover:underline focus:outline-none"
            >
              {selectedLocations.length === availableCities.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
            </button>
          </div>
        </>
      )}
      
      {!loading && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
          {availableCities.map((city) => (
            <button
              key={city.id}
              onClick={() => handleLocationSelect(city.name)}
              className={`py-2 px-4 rounded-md transition-colors ${
                selectedLocations.includes(city.name)
                  ? 'bg-xervix-primary text-white'
                  : 'bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark text-xervix-text-primary dark:text-xervix-text-primary-dark'
              }`}
            >
              {city.name}
            </button>
          ))}
        </div>
      )}
      
      <div className="flex justify-center gap-4 mt-8">
        <CancelButton />
        <button
          onClick={handleSubmit}
          disabled={selectedLocations.length === 0}
          className={`px-8 py-3 rounded-lg font-medium transition-colors ${
            selectedLocations.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-xervix-primary text-white hover:bg-opacity-90'
          }`}
        >
          Continuar
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default LocationSelection;
